import { ReactNode, isValidElement } from 'react';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import {
  BLOCKS, Document as RichtextDocument, INLINES, MARKS
} from '@contentful/rich-text-types';

import {
  Text,
  Link,
  allowListed,
  External
} from '@comicrelief/component-library';

import { RichtextChildPropsObject } from './types';

import { heading, bold } from './formatterFunctions';

// Handy-dandy helper function:
const isAnchor = (link: string | undefined) => link?.startsWith('#');

const options:Options = {
  renderMark: {
    [MARKS.BOLD]: (text: ReactNode) => bold(text),
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (_node, children) => heading(children, 'h1', 'super', '3rem'),
    [BLOCKS.HEADING_2]: (_node, children) => heading(children, 'h2', 'xxl', '3rem'),
    [BLOCKS.HEADING_3]: (_node, children) => heading(children, 'h3', 'xl', '2rem'),
    [BLOCKS.HEADING_4]: (_node, children) => heading(children, 'h4', 'm', '1.25rem'),
    [BLOCKS.HEADING_5]: (_node, children) => heading(children, 'h5', 's', '1rem'),
    [BLOCKS.HEADING_6]: (_node, children) => heading(children, 'h6', 's', '1rem'),
    [INLINES.HYPERLINK]: (node, children) => {
      const { data } = node;
      const externaLink = data?.uri as string ?? '';
      const isInternalLink = allowListed(externaLink);
      const isInternalPath = externaLink.startsWith('/');
      const target = isInternalLink || isAnchor(externaLink) ? 'self' : 'blank';
      const noopener = isInternalLink || isAnchor(externaLink) ? undefined : 'noopener';
      // Keep things simple and non-context specific for now; the alternative is 'standard_white'
      const thisLinkType = 'standard';

      if (isInternalLink || isInternalPath) {
        return (
          <Link type={thisLinkType} href={externaLink} color="red">
            {children}
          </Link>
        );
      }

      return (
        <Link
          type={thisLinkType}
          href={externaLink}
          target={target}
          rel={noopener}
          color="red"
        >
          {children}
        </Link>
      );
    },

    [BLOCKS.PARAGRAPH]: (_node, children) => {
      // We expect paragraph blocks to always have an array of children
      if (!Array.isArray(children)) {
        return null;
      }
      const {
        0: firstChild, 1: secondChild, 2: thirdChild, length
      } = children as ReactNode[];

      // Don't try to render anything without content
      if (firstChild === '' && length === 1) return null;

      // Keep things simple and non-context specific for now:
      const thisButtonColour = 'red';
      const thisMobileButtonColour = 'red';
      const thisIconColour = 'white';
      const thisMobileIconColour = 'white';
      let buttonElement;

      const thisSecondChild = secondChild as RichtextChildPropsObject;

      // Match the pattern seen when a standalone Link is output:
      const isAStandaloneLink = (
        firstChild === ''
        && thirdChild === ''
        && isValidElement(secondChild)
        && thisSecondChild?.props?.href
      ) as boolean;

      // If so, change from the default 'standard' inline Link to a button with the appropriate colour and icon:
      if (isAStandaloneLink) {
        // As we'd never be linking to other content within the Donate application, we can presume it's always an External,
        buttonElement = (
          <Link
            type="button"
            color={thisButtonColour}
            mobileColour={thisMobileButtonColour}
            href={thisSecondChild?.props?.href}
            target="blank"
            icon={(
              <External
                colour={thisIconColour}
                mobileColour={thisMobileIconColour}
              />
            )}
          >
            {thisSecondChild?.props?.children}
          </Link>
        );
      }

      return (
        <Text
          tag="p"
          family="Montserrat"
          color="black"
          size="md"
        >
          {buttonElement ?? children}
        </Text>
      );
    },
  }
};

const RichTextConverter = (richtextToRender: RichtextDocument) => documentToReactComponents(richtextToRender, options);

export { RichTextConverter };
